import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import withRouter from '../Components/Common/withRouter';

//import Components
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import RightSidebar from '../Components/Common/RightSidebar';
import DisplayNamePrompt from '../Components/Common/DisplayNamePrompt';

//import actions
import {
    changeLayout,
    changeSidebarTheme,
    changeLayoutMode,
    changeLayoutWidth,
    changeLayoutPosition,
    changeTopbarTheme,
    changeLeftsidebarSizeType,
    changeLeftsidebarViewType,
    changeSidebarImageType,
    changeSidebarVisibility
} from "../slices/thunks";

//redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from 'reselect';

const Layout = (props) => {
    const [headerClass, setHeaderClass] = useState("");
    // Detect clean/embed mode from URL to hide all chrome for captures/embeds.
    // Use same rules as Report page, and add a tolerant fallback so malformed URLs like
    // `/prediction/72809&clean=true` are recognised.
    const computeIsClean = () => {
        try {
                        // Support malformed URLs that don't populate location.search (e.g. '/prediction/123&clean=true')
                        const rawSearch = window.location.search || (() => {
                            const href = (window.location && window.location.href) ? window.location.href : '';
                            if (!href) return '';
                            if (href.includes('?')) return href.split('?')[1] || '';
                            // If there is no '?', but an '&' was appended (common mistake), take the tail after the first '&'
                            if (href.includes('&')) return href.split('&').slice(1).join('&') || '';
                            return '';
                        })();
                        const searchParams = new URLSearchParams(rawSearch || "");
            const clean = (searchParams.get('clean') || '').toString().toLowerCase();
            const ui = (searchParams.get('ui') || '').toString().toLowerCase();
            const embed = (searchParams.get('embed') || '').toString().toLowerCase();
            const bySearch = clean === '1' || clean === 'true' || ui === 'clean' || ui === 'embed' || embed === '1' || embed === 'true';
            if (bySearch) return true;
            // fallback: try to find parameters anywhere in the full href (cover cases where query is appended incorrectly)
            const href = (window.location.href || '').toString().toLowerCase();
            // looks for e.g. '?clean=true', '&clean=true' or '/...&clean=true' etc.
            const m = href.match(/(?:[?&/])(clean|ui|embed)=([^&#/]+)/i);
            if (!m) return false;
            const key = (m[1] || '').toLowerCase();
            const val = (m[2] || '').toLowerCase();
            if (key === 'clean' || key === 'embed') return val === '1' || val === 'true';
            if (key === 'ui') return val === 'clean' || val === 'embed';
            return false;
        } catch (e) { return false; }
    };
    const isCleanMode = computeIsClean();
    const dispatch = useDispatch();

    const selectLayoutState = (state) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutType: layout.layoutType,
            leftSidebarType: layout.leftSidebarType,
            layoutModeType: layout.layoutModeType,
            layoutWidthType: layout.layoutWidthType,
            layoutPositionType: layout.layoutPositionType,
            topbarThemeType: layout.topbarThemeType,
            leftsidbarSizeType: layout.leftsidbarSizeType,
            leftSidebarViewType: layout.leftSidebarViewType,
            leftSidebarImageType: layout.leftSidebarImageType,
            preloader: layout.preloader,
            sidebarVisibilitytype: layout.sidebarVisibilitytype,
        })
    );
    // Inside your component
    const {
        layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        preloader,
        sidebarVisibilitytype
    } = useSelector(selectLayoutProperties);

    /*
    layout settings
    */
    useEffect(() => {
        if (
            layoutType ||
            leftSidebarType ||
            layoutModeType ||
            layoutWidthType ||
            layoutPositionType ||
            topbarThemeType ||
            leftsidbarSizeType ||
            leftSidebarViewType ||
            leftSidebarImageType ||
            sidebarVisibilitytype
        ) {
            window.dispatchEvent(new Event('resize'));
            dispatch(changeLeftsidebarViewType(leftSidebarViewType));
            dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
            dispatch(changeSidebarTheme(leftSidebarType));
            dispatch(changeLayoutMode(layoutModeType));
            dispatch(changeLayoutWidth(layoutWidthType));
            dispatch(changeLayoutPosition(layoutPositionType));
            dispatch(changeTopbarTheme(topbarThemeType));
            dispatch(changeLayout(layoutType));
            dispatch(changeSidebarImageType(leftSidebarImageType));
            dispatch(changeSidebarVisibility(sidebarVisibilitytype));
        }
    }, [layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype,
        dispatch]);
    /*
    call dark/light mode
    */
    const onChangeLayoutMode = (value) => {
        if (changeLayoutMode) {
            dispatch(changeLayoutMode(value));
        }
    };

    // class add remove in header 
    useEffect(() => {
        window.addEventListener("scroll", scrollNavigation, true);
    });

    function scrollNavigation() {
        var scrollup = document.documentElement.scrollTop;
        if (scrollup > 50) {
            setHeaderClass("topbar-shadow");
        } else {
            setHeaderClass("");
        }
    }

    useEffect(() => {
        if (isCleanMode) return; // no header/sidebar in clean mode
        const el = document.querySelector(".hamburger-icon");
        if (sidebarVisibilitytype === 'show' || layoutType === "vertical" || layoutType === "twocolumn") {
            if (el && el.classList) el.classList.remove('open');
        } else {
            if (el && el.classList) el.classList.add('open');
        }
    }, [sidebarVisibilitytype, layoutType, isCleanMode]);

    return (
        <React.Fragment>
            {isCleanMode ? (
                <>
                    {/* Minimal wrapper for clean/embed mode. No header/sidebar/footer/right sidebar. */}
                    <style>
                        {`
                        /* Clean mode resets */
                        .report-clean-mode { min-height: 100vh; background: #ffffff; color: #212529; padding-left: 24px; padding-right: 24px; }
                        @media (min-width: 768px) { .report-clean-mode { padding-left: 32px; padding-right: 32px; } }
                        @media (min-width: 1200px) { .report-clean-mode { padding-left: 48px; padding-right: 48px; } }
                        .report-clean-mode .report-content { width: 100% !important; max-width: none !important; }
                        `}
                    </style>
                    <div id="capture" className="report-clean-mode">
                        {props.children}
                    </div>
                </>
            ) : (
                <>
                    <div id="layout-wrapper">
                        <Header
                            headerClass={headerClass}
                            layoutModeType={layoutModeType}
                            onChangeLayoutMode={onChangeLayoutMode} />
                        <Sidebar
                            layoutType={layoutType}
                        />
                        <div className="main-content">
                            {props.children}
                            <Footer />
                        </div>
                    </div>
                    <RightSidebar />
                    {/* Asks a newly signed-in member to pick a display name.
                        Never in clean mode - that path renders screenshots. */}
                    <DisplayNamePrompt />
                </>
            )}
        </React.Fragment>

    );
};

Layout.propTypes = {
    children: PropTypes.object,
};

export default withRouter(Layout);